import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CrosshairMode } from 'lightweight-charts';
import './StockChart.css';
import { ChartConfig } from '../screener/ScreenerTable.tsx';
import { CandleResponse } from '../../services/screenerApi';

interface StockChartProps {
  symbol?: string;
  exchange?: string;
  timeframe?: string;
  width?: number;
  height?: number;
  capturedPrice?: string;
  capturedOn?: string;
  chartConfig?: ChartConfig;
  candles?: CandleResponse[] | null;  // Candle data from API
  smaValues?: number[];
}

const SMA_COLORS = ['#FF6B00', '#2962FF', '#00C853', '#FF4081', '#AA00FF'];

const StockChart: React.FC<StockChartProps> = ({
  symbol = 'RELIANCE',
  exchange = 'NSE',
  timeframe = '1D',
  width = 800,
  height = 400,
  capturedPrice,
  capturedOn,
  chartConfig,
  candles,
  smaValues = [],
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const [priceData, setPriceData] = useState<any>({
    open: 0,
    high: 0,
    low: 0,
    close: 0,
    prevClose: 0,
  });
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.body.classList.contains('dark-theme')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('dark-theme'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Check if we have valid candle data
  const hasValidCandles = candles && candles.length > 0;

  useEffect(() => {
    // Skip chart creation if no candles or no container
    if (!chartContainerRef.current || !hasValidCandles) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth || width,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'rgba(197, 203, 206, 0.1)' },
        horzLines: { color: 'rgba(197, 203, 206, 0.1)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
      },
      timeScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
        timeVisible: false,
        secondsVisible: false,
        rightOffset: 8,
        barSpacing: 10,
      },
    });

    chartRef.current = chart;

    // Add candlestick series
    const candlestickSeries = (chart as any).addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    // Use candles from props - we already verified candles exist above
    const chartCandleData = candles!.map(c => ({
      time: c.time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));

    candlestickSeries.setData(chartCandleData);


    const calculateSMA = (data: any[], period: number) => {
      const sma = [];
      for (let i = period - 1; i < data.length; i++) {
        let sum = 0;
        for (let j = 0; j < period; j++) sum += data[i - j].close;
        sma.push({ time: data[i].time, value: sum / period });
      }
      return sma;
    };

    smaValues.forEach((period, idx) => {
      const series = (chart as any).addLineSeries({
        color: SMA_COLORS[idx % SMA_COLORS.length],
        lineWidth: 1,
        title: `SMA ${period}`,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      series.setData(calculateSMA(chartCandleData, period));
    });

    // Add captured price line if enabled
    if (chartConfig?.capturedPriceLine && capturedPrice && capturedOn) {
      console.log('Captured Price Line Config:', { capturedPrice, capturedOn, chartConfig });

      // Parse captured price (remove currency symbol)
      const priceValue = parseFloat(capturedPrice.replace(/[^0-9.-]+/g, ''));
      console.log('Parsed Price Value:', priceValue);

      // Parse capturedOn date (format: "DD MMM YYYY" like "11 Dec 2024")
      const parseCapturedDate = (dateStr: string): string | null => {
        try {
          const months: { [key: string]: number } = {
            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
          };

          const parts = dateStr.split(' ');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = months[parts[1]];
            const year = parseInt(parts[2]);

            const date = new Date(year, month, day);
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');

            return `${yyyy}-${mm}-${dd}`;
          }
          return null;
        } catch {
          return null;
        }
      };

      const capturedDateFormatted = parseCapturedDate(capturedOn);
      console.log('Captured Date Formatted:', capturedDateFormatted);

      if (capturedDateFormatted) {
        // Find the index of the captured date in chartCandleData
        const capturedIndex = chartCandleData.findIndex((d: any) => d.time === capturedDateFormatted);
        console.log('Captured Index:', capturedIndex, 'Looking for:', capturedDateFormatted);

        if (capturedIndex !== -1) {
          // Create line data: 3 bars before and 3 bars after (excluding captured date)
          const lineData = [];

          // Add 3 bars before
          for (let i = Math.max(0, capturedIndex - 4); i <= capturedIndex; i++) {
            lineData.push({
              time: chartCandleData[i].time,
              value: priceValue
            });
          }

          // Skip the captured date candle (capturedIndex)

          // Add 3 bars after
          for (let i = capturedIndex + 1; i <= Math.min(chartCandleData.length - 1, capturedIndex + 3); i++) {
            lineData.push({
              time: chartCandleData[i].time,
              value: priceValue
            });
          }

          // Draw the captured price line
          if (lineData.length > 0) {
            console.log('Drawing Captured Price Line with data:', lineData);
            const capturedPriceLine = (chart as any).addLineSeries({
              color: '#b300faff',
              lineWidth: 1,
              lineStyle: 0,
              priceLineVisible: false,
              lastValueVisible: false,
              crosshairMarkerVisible: false,
              lineColor: '#000000ff',
              titleColor: '#9C27B0',
            });
            capturedPriceLine.setData(lineData);

            // Anchor marker 6 candles before captured date
            const markerIdx = Math.max(0, capturedIndex - 5);
            const markerTime = chartCandleData[markerIdx].time;
            candlestickSeries.setMarkers([
              {
                time: markerTime,
                position: 'aboveBar',
                color: '#9C27B0',
                shape: 'text',
                text: `Captured at ₹${priceValue.toFixed(2)}`,
              },
              {
                time: markerTime,
                position: 'aboveBar',
                color: '#888888',
                shape: 'text',
                text: `📅 ${capturedOn}`,
              },
            ]);

            console.log('Captured Price Line drawn successfully');
          } else {
            console.log('No line data generated');
          }
        }
      }
    }

    // Set initial price data from last candle
    const lastCandle = chartCandleData[chartCandleData.length - 1];
    const prevCandle = chartCandleData[chartCandleData.length - 2];
    setPriceData({
      open: lastCandle.open,
      high: lastCandle.high,
      low: lastCandle.low,
      close: lastCandle.close,
      prevClose: prevCandle.close,
    });

    // Update price data on crosshair move
    chart.subscribeCrosshairMove((param: any) => {
      if (!param || !param.time) {
        // Reset to last candle data
        setPriceData({
          open: lastCandle.open,
          high: lastCandle.high,
          low: lastCandle.low,
          close: lastCandle.close,
          prevClose: prevCandle.close,
        });
        return;
      }

      // Try to get data from seriesData (v4 API)
      if (param.seriesData && param.seriesData.size > 0) {
        const iterator = param.seriesData.values();
        const data = iterator.next().value;

        if (data && data.open !== undefined) {
          const currentIndex = chartCandleData.findIndex((d: any) => d.time === param.time);
          const prevClose = currentIndex > 0 ? chartCandleData[currentIndex - 1].close : -1;

          setPriceData({
            open: data.open,
            high: data.high,
            low: data.low,
            close: data.close,
            prevClose: prevClose,
          });
        }
      }
      // Fallback: try seriesPrices (different API version)
      else if (param.seriesPrices && param.seriesPrices.size > 0) {
        const prices = param.seriesPrices.get(candlestickSeries);

        if (prices && prices.open !== undefined) {
          const currentIndex = chartCandleData.findIndex((d: any) => d.time === param.time);
          const prevClose = currentIndex > 0 ? chartCandleData[currentIndex - 1].close : -1;

          setPriceData({
            open: prices.open,
            high: prices.high,
            low: prices.low,
            close: prices.close,
            prevClose: prevClose,
          });
        }
      }
    });

    // Add captured price rectangle if enabled
    if (chartConfig?.capturedPriceRectangle && capturedPrice && capturedOn) {
      const priceValue = parseFloat(capturedPrice.replace(/[^0-9.-]+/g, ''));
      const bottomPrice = priceValue * 0.95;
      const rectFillColor = isDarkMode ? 'rgba(34, 211, 238, 0.18)' : 'rgba(179, 0, 250, 0.15)';

      const parseCapturedDate = (dateStr: string): string | null => {
        try {
          const months: { [key: string]: number } = {
            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
          };
          const parts = dateStr.split(' ');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = months[parts[1]];
            const year = parseInt(parts[2]);
            const date = new Date(year, month, day);
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          }
          return null;
        } catch {
          return null;
        }
      };

      const capturedDateFormatted = parseCapturedDate(capturedOn);
      if (capturedDateFormatted) {
        const capturedIndex = chartCandleData.findIndex((d: any) => d.time === capturedDateFormatted);
        if (capturedIndex !== -1) {
          const rectData = [];
          for (let i = Math.max(0, capturedIndex - 4); i <= Math.min(chartCandleData.length - 1, capturedIndex + 3); i++) {
            rectData.push({ time: chartCandleData[i].time, value: priceValue });
          }

          if (rectData.length > 0) {
            const rectSeries = (chart as any).addBaselineSeries({
              baseValue: { type: 'price', price: bottomPrice },
              topFillColor1: rectFillColor,
              topFillColor2: rectFillColor,
              topLineColor: 'rgba(0, 0, 0, 0)',
              bottomFillColor1: 'rgba(0, 0, 0, 0)',
              bottomFillColor2: 'rgba(0, 0, 0, 0)',
              bottomLineColor: 'rgba(0, 0, 0, 0)',
              lineWidth: 0,
              priceLineVisible: false,
              lastValueVisible: false,
              crosshairMarkerVisible: false,
            });
            rectSeries.setData(rectData);

            // Anchor marker 6 candles before captured date
            const markerIdx = Math.max(0, capturedIndex - 5);
            candlestickSeries.setMarkers([
              {
                time: chartCandleData[markerIdx].time,
                position: 'aboveBar',
                color: '#888888',
                shape: 'text',
                text: `📅 ${capturedOn}`,
              },
            ]);
          }
        }
      }
    }

    // Add Support Line (your analysis)
    // const supportLine = (chart as any).addLineSeries({
    //   color: '#26a69a',
    //   lineWidth: 2,
    //   lineStyle: 2, // dashed
    //   title: 'Support',
    // });
    // supportLine.setData([
    //   { time: '2024-01-01', value: 1850 },
    //   { time: '2024-01-26', value: 1850 },
    // ]);

    // // Add Resistance Line (your analysis)
    // const resistanceLine = (chart as any).addLineSeries({
    //   color: '#ef5350',
    //   lineWidth: 2,
    //   lineStyle: 2, // dashed
    //   title: 'Resistance',
    // });
    // resistanceLine.setData([
    //   { time: '2024-01-01', value: 2100 },
    //   { time: '2024-01-26', value: 2100 },
    // ]);

    // // Add Trendline (your analysis)
    // const trendLine = (chart as any).addLineSeries({
    //   color: '#2962FF',
    //   lineWidth: 2,
    //   title: 'Trend',
    // });
    // trendLine.setData([
    //   { time: '2024-01-01', value: 1800 },
    //   { time: '2024-01-26', value: 2200 },
    // ]);

    // // Add Markers for patterns you identified
    // candlestickSeries.setMarkers([
    //   {
    //     time: '2024-01-05',
    //     position: 'belowBar',
    //     color: '#26a69a',
    //     shape: 'arrowUp',
    //     text: 'Bullish Pattern',
    //   },
    //   {
    //     time: '2024-01-17',
    //     position: 'aboveBar',
    //     color: '#2962FF',
    //     shape: 'circle',
    //     text: 'Breakout',
    //   },
    //   {
    //     time: '2024-01-25',
    //     position: 'aboveBar',
    //     color: '#f59e0b',
    //     shape: 'arrowDown',
    //     text: 'Resistance Test',
    //   },
    // ]);

    // Add Volume series
    const volumeSeries = (chart as any).addHistogramSeries({
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
    });

    volumeSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });

    // Use actual volume from candles
    const volumeData = candles!.map((c) => ({
      time: c.time,
      value: c.volume || 0,
      color: c.close > c.open ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)',
    }));

    volumeSeries.setData(volumeData);

    // Cleanup
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [capturedPrice, capturedOn, chartConfig?.capturedPriceLine, chartConfig?.capturedPriceRectangle, candles, isDarkMode]);

  // Resize-only effect — updates width/height without recreating the chart
  useEffect(() => {
    if (chartRef.current) {
      const timeScale = chartRef.current.timeScale();
      const visibleRange = timeScale.getVisibleLogicalRange();
      chartRef.current.applyOptions({ width, height });
      if (visibleRange) {
        timeScale.setVisibleLogicalRange(visibleRange);
      }
    }
  }, [width, height]);

  // Calculate change and percentage
  const change = priceData.close - priceData.prevClose;
  const changePercent = priceData.prevClose > 0
    ? ((change / priceData.prevClose) * 100)
    : 0;
  const isPositive = change >= 0;

  // Determine if current candle is bullish (green) or bearish (red)
  const isBullish = priceData.close >= priceData.open;

  // If no candles, show message instead of chart
  if (!hasValidCandles) {
    return (
      <div className="stock-chart-wrapper">
        <div className="stock-chart-header">
          <div className="symbol-info">
            <span className="symbol-name">{symbol}</span>
            <span className="symbol-timeframe">
              {timeframe === 'Daily' ? 'D' : timeframe === 'Weekly' ? 'W' : timeframe === 'Monthly' ? 'M' : timeframe}
            </span>
            <span className="symbol-exchange">{exchange}</span>
          </div>
        </div>
        <div
          className="stock-chart-no-data"
          style={{
            width: width,
            height: height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            color: '#888',
            fontSize: '14px',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '24px' }}>📊</span>
          <span>No chart data available</span>
          <span style={{ fontSize: '12px', color: '#666' }}>Run historical sync to load data</span>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-chart-wrapper">
      {/* TradingView-style header */}
      <div className="stock-chart-header">
        <div className="symbol-info">
          <span className="symbol-name">{symbol}</span>
          <span className="symbol-timeframe">
            {timeframe === 'Daily' ? 'D' : timeframe === 'Weekly' ? 'W' : timeframe === 'Monthly' ? 'M' : timeframe}
          </span>
          <span className="symbol-exchange">{exchange}</span>
        </div>
        <div className="price-info">
          <span className="price-pair">
            <span className="price-label">O</span>
            <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.open.toFixed(2)}</span>
          </span>
          <span className="price-pair">
            <span className="price-label">H</span>
            <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.high.toFixed(2)}</span>
          </span>
          <span className="price-pair">
            <span className="price-label">L</span>
            <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.low.toFixed(2)}</span>
          </span>
          <span className="price-pair">
            <span className="price-label">C</span>
            <span className={`price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.close.toFixed(2)}</span>
          </span>
          <span className={`stock-price-change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
          </span>
        </div>
      </div>

      {/* Chart container */}
      <div ref={chartContainerRef} className="stock-chart-container" />
    </div>
  );
};

export default StockChart;
